import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { 
  submitStudentRating, 
  checkRatingEligibility,
  validateRatingInputs 
} from '../services/ratingService';

const StudentRatingForm = ({ 
  student, 
  projectId, 
  projectTitle,
  companyName,
  onRatingSubmitted = () => {},
  onCancel = () => {} 
}) => {
  const { currentUser } = useContext(AuthContext);
  
  // State management
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  // Rating form data
  const [ratingData, setRatingData] = useState({
    overallRating: 5,
    categoryRatings: {
      technicalSkills: 5,
      communication: 5,
      workQuality: 5,
      timeliness: 5,
      professionalism: 5
    },
    review: '',
    wouldWorkAgain: true
  });

  // Check eligibility on component mount
  useEffect(() => {
    if (currentUser && student && projectId) {
      checkEligibility();
    }
  }, [currentUser, student, projectId]);

  const checkEligibility = async () => {
    try {
      setLoading(true);
      const eligibilityResult = await checkRatingEligibility(
        currentUser.uid, 
        student.id, 
        projectId
      );
      setEligibility(eligibilityResult);
    } catch (error) {
      console.error('Error checking eligibility:', error);
      setError('Error checking rating eligibility');
    } finally {
      setLoading(false);
    }
  };

  // Handle star rating click
  const handleStarClick = (field, value) => {
    if (field === 'overall') {
      setRatingData({
        ...ratingData,
        overallRating: value
      });
    } else {
      setRatingData({
        ...ratingData,
        categoryRatings: {
          ...ratingData.categoryRatings,
          [field]: value
        }
      });
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate inputs
    const validationErrors = validateRatingInputs(ratingData);
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    try {
      setSubmitting(true);

      const submissionData = {
        studentId: student.id,
        companyId: currentUser.uid,
        projectId,
        companyName: companyName || 'Company',
        projectTitle: projectTitle || 'Project',
        ...ratingData
      };

      await submitStudentRating(submissionData);
      
      setSuccess(true);
      setTimeout(() => {
        onRatingSubmitted();
      }, 2000);

    } catch (error) {
      console.error('Error submitting rating:', error);
      setError(error.message || 'Failed to submit rating');
    } finally {
      setSubmitting(false);
    }
  };

  // Render star rating
  const renderStarRating = (field, value, label) => {
    return (
      <div style={{ marginBottom: '20px' }}>
        <label style={{ 
          display: 'block', 
          marginBottom: '8px', 
          fontWeight: '500',
          fontSize: '14px'
        }}>
          {label}
        </label>
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          {[1, 2, 3, 4, 5].map(star => (
            <button
              key={star}
              type="button"
              onClick={() => handleStarClick(field, star)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: star <= value ? '#fbbf24' : '#e5e7eb',
                transition: 'color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (star <= value) return;
                e.target.style.color = '#fde047';
              }}
              onMouseLeave={(e) => {
                e.target.style.color = star <= value ? '#fbbf24' : '#e5e7eb';
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" style={{ color: star <= value ? '#fbbf24' : '#e5e7eb' }}>
                <polygon points="12,2 15.09,8.26 22,9 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9 8.91,8.26"/>
              </svg>
            </button>
          ))}
          <span style={{ marginLeft: '12px', fontSize: '14px', color: '#666' }}>
            {value}/5
          </span>
        </div>
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        textAlign: 'center'
      }}>
        <div style={{ color: '#666' }}>Checking rating eligibility...</div>
      </div>
    );
  }

  // Not eligible to rate
  if (eligibility && !eligibility.canRate) {
    return (
      <div style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        textAlign: 'center'
      }}>
        <div style={{ 
          fontSize: '48px', 
          marginBottom: '16px' 
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px', color: '#f59e0b' }}>
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        </div>
        <h3 style={{ 
          color: '#dc2626',
          marginBottom: '12px',
          fontSize: '18px',
          fontWeight: 'bold'
        }}>
          Cannot Rate Student
        </h3>
        <p style={{ color: '#666', marginBottom: '16px' }}>
          {eligibility.reason}
        </p>
        <button
          onClick={onCancel}
          style={{
            backgroundColor: '#6b7280',
            color: 'white',
            padding: '10px 20px',
            borderRadius: '8px',
            border: 'none',
            fontSize: '14px',
            cursor: 'pointer'
          }}
        >
          Close
        </button>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        textAlign: 'center'
      }}>
        <div style={{ 
          fontSize: '48px', 
          marginBottom: '16px' 
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px', color: '#10b981' }}>
            <polyline points="20,6 9,17 4,12"/>
          </svg>
        </div>
        <h3 style={{ 
          color: '#10b981',
          marginBottom: '12px',
          fontSize: '18px',
          fontWeight: 'bold'
        }}>
          Rating Submitted Successfully!
        </h3>
        <p style={{ color: '#666' }}>
          Thank you for rating {student.fullName || student.firstName || 'the student'}. 
          Your feedback helps improve the platform.
        </p>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: 'white',
      padding: '24px',
      borderRadius: '12px',
      border: '1px solid #e5e7eb'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h3 style={{ 
          fontSize: '20px',
          fontWeight: 'bold',
          marginBottom: '8px',
          color: '#1a1a1a'
        }}>
          Rate {student.fullName || student.firstName || 'Student'}
        </h3>
        <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>
          Project: {projectTitle} • Please provide honest feedback to help the student grow
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div style={{
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          color: '#dc2626',
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '20px',
          fontSize: '14px'
        }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Overall Rating */}
        <div style={{ 
          marginBottom: '32px',
          padding: '20px',
          backgroundColor: '#f8fafc',
          borderRadius: '8px'
        }}>
          <h4 style={{ 
            marginBottom: '16px',
            fontSize: '16px',
            fontWeight: 'bold',
            color: '#1a1a1a'
          }}>
            Overall Rating
          </h4>
          {renderStarRating('overall', ratingData.overallRating, 'How would you rate this student overall?')}
        </div>

        {/* Category Ratings */}
        <div style={{ marginBottom: '32px' }}>
          <h4 style={{ 
            marginBottom: '20px',
            fontSize: '16px',
            fontWeight: 'bold',
            color: '#1a1a1a'
          }}>
            Detailed Ratings
          </h4>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
            gap: '16px'
          }}>
            {renderStarRating('technicalSkills', ratingData.categoryRatings.technicalSkills, 'Technical Skills')}
            {renderStarRating('communication', ratingData.categoryRatings.communication, 'Communication')}
            {renderStarRating('workQuality', ratingData.categoryRatings.workQuality, 'Work Quality')}
            {renderStarRating('timeliness', ratingData.categoryRatings.timeliness, 'Timeliness')}
            {renderStarRating('professionalism', ratingData.categoryRatings.professionalism, 'Professionalism')}
          </div>
        </div>

        {/* Written Review */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            fontWeight: '500',
            fontSize: '14px'
          }}>
            Written Review *
          </label>
          <textarea
            value={ratingData.review}
            onChange={(e) => setRatingData({ ...ratingData, review: e.target.value })}
            placeholder="Share your experience working with this student. What did they do well? What could they improve? Be specific and constructive."
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              border: '1px solid #d1d5db',
              minHeight: '120px',
              fontSize: '14px',
              fontFamily: 'inherit',
              resize: 'vertical'
            }}
            required
          />
          <p style={{ 
            fontSize: '12px', 
            color: '#666', 
            marginTop: '4px',
            margin: '4px 0 0 0'
          }}>
            Minimum 10 characters. Be constructive and professional.
          </p>
        </div>

        {/* Would Work Again */}
        <div style={{ marginBottom: '32px' }}>
          <label style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            <input
              type="checkbox"
              checked={ratingData.wouldWorkAgain}
              onChange={(e) => setRatingData({ ...ratingData, wouldWorkAgain: e.target.checked })}
              style={{ width: '16px', height: '16px' }}
            />
            I would work with this student again
          </label>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              backgroundColor: 'transparent',
              color: '#6b7280',
              padding: '12px 24px',
              borderRadius: '8px',
              border: '1px solid #d1d5db',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            style={{
              backgroundColor: submitting ? '#9ca3af' : '#6366f1',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: submitting ? 'not-allowed' : 'pointer'
            }}
          >
            {submitting ? 'Submitting Rating...' : 'Submit Rating'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default StudentRatingForm; 